import { RemoteType } from 'data/App/types'
import {
  AccountType,
  BeneficiaryType,
  ErrorType,
  WhitelistAddressType,
  WhitelistCapabilitiesType
} from 'data/types'

import { PaymentsActionType } from './actions'
import * as AT from './actionTypes'
import { PaymentBanksActionType } from './BankTransfer/actions'
import * as bankTransferActionTypes from './BankTransfer/actionTypes'
import bankTransferReducer, {
  INITIAL_STATE as bankTransferInitialState,
  PaymentBanksStateType
} from './BankTransfer/reducer'
import paymentCurrenciesReducer, {
  actions as paymentCurrenciesActions,
  initialState as paymentCurrenciesInitialState,
  PaymentCurrenciesState
} from './Currencies/slice'
import { ElegiblePMsActionType } from './Elegible/actions'
import * as eligibleActionTypes from './Elegible/actionTypes'
import eligibleReducer, {
  EligiblePaymentTypeState,
  INITIAL_STATE as eligibleInitialState
} from './Elegible/reducer'
import { PaymentMethodsActionType } from './Methods/actions'
import * as methodActionTypes from './Methods/actionTypes'
import methodsReducer, { PaymentMethodsStateType } from './Methods/reducers'
import { PaymentActions } from './Payment/actions'
import * as paymentAction from './Payment/actionTypes'
import paymentReducer, { PaymentState } from './Payment/reducers'
import { SubmitDepositActions } from './SubmitDeposit/actions'
import * as submitDepositActionTypes from './SubmitDeposit/actionTypes'
import submitDepositReducer, {
  INITIAL_STATE as submitDepositInitialState,
  SubmitDepositState
} from './SubmitDeposit/reducers'
import { TransactionsActions } from './Transactions/actions'
import * as transactionsActionTypes from './Transactions/actionTypes'
import transactionsReducer, { TransactionsState } from './Transactions/reducers'
import paymentsWithdrawalReducer, {
  actions as withdrawalActions,
  initialState as withdrawalInitialState,
  PaymentsWithdrawalState
} from './Withdrawal/slice'
import { WithdrawFeesAnMinActions } from './WithdrawFeesAndMin/actions'
import * as withdrawFeesAndMinActionTypes from './WithdrawFeesAndMin/actionTypes'
import withdrawFeesAndMin, {
  INITIAL_STATE as withdrawFeesAndMinInitialState,
  WithdrawFeesAnMinState
} from './WithdrawFeesAndMin/reducers'

export type PaymentsStateType = {
  // ACCOUNTS
  accounts: {
    [currency: string]: RemoteType<AccountType>
  }
  bankTransfer: PaymentBanksStateType
  // BENEFICIARIES
  beneficiaries: BeneficiaryType[]
  beneficiariesError?: ErrorType
  beneficiariesLoading: boolean
  currencies: PaymentCurrenciesState
  eligible: EligiblePaymentTypeState
  methods: PaymentMethodsStateType
  payment: PaymentState
  submitDeposit: SubmitDepositState
  transactions: TransactionsState
  // ADDRESS WHITELISTING
  whitelistAddresses: WhitelistAddressType[]
  whitelistAddressesEnabled: boolean
  whitelistCapabilities: WhitelistCapabilitiesType[]
  withdrawFeesAndMin: WithdrawFeesAnMinState
  withdrawal: PaymentsWithdrawalState
}

const initialState: PaymentsStateType = {
  // ACCOUNTS
  accounts: {},
  bankTransfer: bankTransferInitialState,
  // BENEFICIARIES
  beneficiaries: [],
  beneficiariesLoading: true,
  currencies: paymentCurrenciesInitialState,
  eligible: eligibleInitialState,
  methods: {},
  payment: {},
  submitDeposit: submitDepositInitialState,
  transactions: {},
  // ADDRESS WHITELISTING
  whitelistAddresses: [],
  whitelistAddressesEnabled: false,
  whitelistCapabilities: [],
  withdrawFeesAndMin: withdrawFeesAndMinInitialState,
  withdrawal: withdrawalInitialState
}

const payments = (
  state: PaymentsStateType = initialState,
  action: PaymentsActionType
): PaymentsStateType => {
  /* Horrible hack to have inner reducers handle types they know, this should be replaced by a combineReducers call */
  if (Object.values(methodActionTypes).find((type) => type === action.type)) {
    const paymentMethodAction = action as PaymentMethodsActionType
    return {
      ...state,
      methods: methodsReducer(state.methods, paymentMethodAction)
    }
  }

  if (Object.values(bankTransferActionTypes).find((type) => type === action.type)) {
    const bankTransferAction = action as PaymentBanksActionType
    return {
      ...state,
      bankTransfer: bankTransferReducer(state.bankTransfer, bankTransferAction)
    }
  }

  if (Object.values(paymentAction).find((type) => type === action.type)) {
    const chargeAction = action as PaymentActions
    return {
      ...state,
      payment: paymentReducer(state.payment, chargeAction)
    }
  }

  if (Object.values(eligibleActionTypes).find((type) => type === action.type)) {
    const eligibleAction = action as ElegiblePMsActionType
    return {
      ...state,
      eligible: eligibleReducer(state.eligible, eligibleAction)
    }
  }

  if (Object.values(submitDepositActionTypes).find((type) => type === action.type)) {
    const submitDepositAction = action as SubmitDepositActions
    return {
      ...state,
      submitDeposit: submitDepositReducer(state.submitDeposit, submitDepositAction)
    }
  }

  if (Object.values(transactionsActionTypes).find((type) => type === action.type)) {
    const transactionsAction = action as TransactionsActions
    return {
      ...state,
      transactions: transactionsReducer(state.transactions, transactionsAction)
    }
  }

  if (Object.values(withdrawFeesAndMinActionTypes).find((type) => type === action.type)) {
    const withdrawAction = action as WithdrawFeesAnMinActions
    return {
      ...state,
      withdrawFeesAndMin: withdrawFeesAndMin(state.withdrawFeesAndMin, withdrawAction)
    }
  }

  if (Object.values(paymentCurrenciesActions).find((a) => a.type === action.type)) {
    return {
      ...state,
      currencies: paymentCurrenciesReducer(state.currencies, action)
    }
  }

  if (Object.values(withdrawalActions).find((a) => a.type === action.type)) {
    return {
      ...state,
      withdrawal: paymentsWithdrawalReducer(state.withdrawal, action)
    }
  }
  /* Horrible hack end */

  switch (action.type) {
    case AT.ACCOUNT_SUCCESS:
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [action.payload.currency]: {
            error: undefined,
            loading: false,
            value: action.payload.account
          }
        }
      }
    case AT.ACCOUNT_LOADING:
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [action.payload.currency]: {
            loading: true
          }
        }
      }
    case AT.ACCOUNT_ERROR:
      return {
        ...state,
        accounts: {
          ...state.accounts,
          [action.payload.currency]: {
            error: action.payload.error,
            loading: false
          }
        }
      }
    case AT.BENEFICIARIES_SUCCESS:
      return {
        ...state,
        beneficiaries: action.payload.beneficiaries,
        beneficiariesError: undefined,
        beneficiariesLoading: false
      }
    case AT.BENEFICIARIES_LOADING:
      return {
        ...state,
        beneficiaries: [],
        beneficiariesError: undefined,
        beneficiariesLoading: true
      }
    case AT.BENEFICIARIES_ERROR:
      return {
        ...state,
        beneficiaries: [],
        beneficiariesError: action.payload.error,
        beneficiariesLoading: false
      }
    case AT.SET_WHITELIST_ADDRESSES:
      return {
        ...state,
        whitelistAddresses: action.payload.addresses
      }
    case AT.SET_WHITELIST_ADDRESSES_ENABLED:
      return {
        ...state,
        whitelistAddressesEnabled: action.payload.enabled
      }
    case AT.SET_WHITELIST_CAPABILITIES:
      return {
        ...state,
        whitelistCapabilities: action.payload.capabilities
      }
    default:
      return state
  }
}

export default payments
